import { useEffect, useRef, useState } from "react";
import { trackEvent } from "@/lib/audit";
import { verifyTurnstile } from "@/lib/antifraud/antifraud.functions";

/**
 * PR-34A — Cloudflare Turnstile widget. Renders nothing (and reports verified)
 * when `VITE_CF_TURNSTILE_SITE_KEY` is not set, so existing UX is unchanged
 * until the secret is provisioned. When the site key IS set, it mounts the
 * Turnstile script and reports server-verified tokens via `onVerified`.
 */
interface Props {
  action: "registro" | "claim_xp" | "guest_to_registered";
  onVerified: (verified: boolean) => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: { sitekey: string; action?: string; callback: (token: string) => void },
      ) => string;
      reset: (id: string) => void;
    };
  }
}

const SITE_KEY = import.meta.env.VITE_CF_TURNSTILE_SITE_KEY as string | undefined;

export function TurnstileGate({ action, onVerified }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const idRef = useRef<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!SITE_KEY) {
      void trackEvent("turnstile_skipped", { action });
      onVerified(true);
      return;
    }
    let cancelled = false;
    const ensureScript = () =>
      new Promise<void>((resolve) => {
        if (window.turnstile) return resolve();
        const existing = document.querySelector<HTMLScriptElement>(
          'script[data-turnstile="1"]',
        );
        if (existing) {
          existing.addEventListener("load", () => resolve(), { once: true });
          return;
        }
        const s = document.createElement("script");
        s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        s.async = true;
        s.defer = true;
        s.dataset.turnstile = "1";
        s.onload = () => resolve();
        document.head.appendChild(s);
      });
    void ensureScript().then(() => {
      if (cancelled || !hostRef.current || !window.turnstile) return;
      idRef.current = window.turnstile.render(hostRef.current, {
        sitekey: SITE_KEY,
        action,
        callback: async (token: string) => {
          try {
            const res = await verifyTurnstile({ data: { token } });
            if (res.ok) {
              void trackEvent("turnstile_verified", { action, skipped: res.skipped === true });
              onVerified(true);
            } else {
              void trackEvent("turnstile_failed", { action, reason: res.reason });
              onVerified(false);
            }
          } catch {
            onVerified(false);
          }
        },
      });
      setMounted(true);
    });
    return () => {
      cancelled = true;
    };
  }, [action, onVerified]);

  if (!SITE_KEY) return null;
  return (
    <div className="my-3 flex justify-center" data-turnstile-host="1">
      <div ref={hostRef} />
      {!mounted ? (
        <div className="text-[11px] opacity-60">Verificando…</div>
      ) : null}
    </div>
  );
}